import {
    AnimationComponent,
    CollisionComponent,
    Material,
    MeshComponent,
    Object3D,
    TextComponent,
    WonderlandEngine,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {
    NetworkConfigurationComponent,
    NetworkedComponent,
    networkManager,
    WonderlandWebsocketEvent,
} from '@wonderlandcloud/client';
import {Bullet, BulletScaling} from './bullet.js';
import {hapticFeedback} from './button.js';
import {Leaderboard} from './leaderboard.js';
import {CHARACTERS} from './register-character.js';
import {TimeoutScreen} from './timeout-screen.js';
import {TimeoutSign} from './timeout-sign.js';

import {SmoothLocomotion} from './player-controller.js';
import {LeaderboardEvent, Team} from '../server/types.js';

const tempVec0 = [0, 0, 0];
const tempVec1 = [0, 0, 0];

import {
    AudioManager,
    AudioSource,
    globalAudioManager,
} from '@wonderlandengine/spatial-audio';
import {vec3} from 'gl-matrix';
import {BulletPool} from './bullet-pool.js';
import {Flag} from './flag.js';
import {RewardScreen} from './reward-screen.js';
import profile from './utils/profile.js';
import {Timer} from './timer.js';
import type {GameAnalytics as GA} from 'gameanalytics';
declare var GameAnalytics: typeof GA;
import {ErrorSeverity, ProgressionStatus} from './game-analytics-enums.js';
import {Config} from './config.js';
import {TeamMaterials, TeamName} from './utils/team.js';
import {Sounds} from './sounds.js';
import {TargetShootingPanel} from './targetShootingPanel.js';
import {Vrm} from '@wonderlandengine/components';
import {PaintballVrmIk} from './paintball-vrm-ik.js';

type UserObjects = {
    head: {
        object: Object3D;
        id: number;
    };
    gun: {object?: Object3D};
};

/**
 * custom-event-handler
 */
export class PaintballClient extends NetworkConfigurationComponent {
    static TypeName = 'paintball-client';

    @property.object()
    playerHead!: Object3D;

    @property.object()
    playerPrototype!: Object3D;

    @property.object({required: true})
    bulletPoolObject!: Object3D;
    bulletPool!: BulletPool;

    @property.object({required: true})
    playerGun!: Object3D;
    @property.object({required: true})
    playerGunVr!: Object3D;
    @property.object({required: true})
    playerHand!: Object3D;
    bulletStart!: Object3D;

    @property.object({required: true})
    leaderboard!: Object3D;

    @property.object({required: true})
    shotSound!: Object3D;

    @property.object()
    notificationsObject!: Object3D;

    @property.object()
    rewardScreen!: Object3D;

    @property.object({required: true})
    hitSound!: Object3D;

    @property.object({required: true})
    timeoutScreen!: Object3D;

    @property.float(3.0)
    notifyTime!: number;

    @property.material({required: true})
    materialRed!: Material;
    @property.material({required: true})
    materialBlue!: Material;

    @property.material({required: true})
    materialTextRed!: Material;
    @property.material({required: true})
    materialTextBlue!: Material;

    @property.material({required: true})
    materialBulletRed!: Material;
    @property.material({required: true})
    materialBulletBlue!: Material;

    @property.object({required: true})
    flagRed!: Object3D;

    @property.object({required: true})
    flagBlue!: Object3D;

    @property.object({required: true})
    startButton!: Object3D;

    @property.object({required: true})
    menuButton!: Object3D;

    flags: Flag[] = [];

    static InheritProperties = true;

    team: Team = Team.RED;

    timeout = false;

    notificationText?: TextComponent | null;
    notificationTimer = 0;

    username: string = '';
    avatar: string = '';

    bulletMap = new Map<number, Object3D>();

    streak = 0;

    audioReady!: AudioSource;
    audioGameStart!: AudioSource;
    audioYouWin!: AudioSource;
    audioStreak!: AudioSource;
    audioStreak10!: AudioSource;
    audioGameEnd!: AudioSource;
    audioGameWon!: AudioSource;
    audioGameLost!: AudioSource;
    audioWelcome!: AudioSource;
    audioFlagSaved!: AudioSource;
    audioFlagStolen!: AudioSource;
    audioFlagTaken!: AudioSource;
    audioScore!: AudioSource;

    audioGotHit!: AudioSource;

    audioMusic!: AudioSource;

    pinAnim!: AnimationComponent;
    magAnim!: AnimationComponent;

    teamBulletMaterials: Material[] = [];
    teamMaterials: Material[] = [];
    teamTextMaterials: Material[] = [];

    connectStart = 0;

    mouseDown = () => {
        this.shoot();
    };

    selectStart = (e: XRInputSourceEvent) => {
        if (e.inputSource.handedness == 'right') {
            this.shoot();
        }
    };

    /**
     * This is a test comment
     */
    override start() {
        // Then connect
        this.notificationText = this.notificationsObject.getComponent(TextComponent);

        this.bulletStart = this.playerGun.findByNameDirect('BulletStart')[0];
        const pistolMagazine = this.playerGun.findByNameDirect('Gun_Pistol_Magazine')[0];
        const pistolPin = this.playerGun.findByNameDirect('Gun_Pistol_Pin')[0];

        this.pinAnim = pistolPin.getComponent(AnimationComponent)!;
        this.magAnim = pistolMagazine.getComponent(AnimationComponent)!;

        this.username = profile.getUsername();
        this.avatar = profile.getAvatar();

        window.addEventListener('mousedown', this.mouseDown);
        this.engine.onXRSessionStart.add((s) => {
            s.addEventListener('selectstart', this.selectStart);

            this.bulletStart = this.playerGunVr.findByNameDirect('BulletStart')[0];
            const pistolMagazine =
                this.playerGunVr.findByNameDirect('Gun_Pistol_Magazine')[0];
            const pistolPin = this.playerGunVr.findByNameDirect('Gun_Pistol_Pin')[0];

            this.pinAnim = pistolPin.getComponent(AnimationComponent)!;
            this.magAnim = pistolMagazine.getComponent(AnimationComponent)!;
            /* This also swaps the active networked component */
            this.playerGunVr.active = true;
            this.playerGun.active = false;
        });

        this.engine.onXRSessionEnd.add(() => {
            this.bulletStart = this.playerGun.findByNameDirect('BulletStart')[0];

            const pistolMagazine =
                this.playerGun.findByNameDirect('Gun_Pistol_Magazine')[0]!;
            const pistolPin = this.playerGun.findByNameDirect('Gun_Pistol_Pin')[0]!;
            this.pinAnim = pistolPin.getComponent(AnimationComponent)!;
            this.magAnim = pistolMagazine.getComponent(AnimationComponent)!;
            /* This also swaps the active networked component */
            this.playerGunVr.active = false;
            this.playerGun.active = true;
        });

        TeamMaterials[Team.RED] = this.materialBulletRed;
        TeamMaterials[Team.BLUE] = this.materialBulletBlue;

        this.teamBulletMaterials = TeamMaterials as Material[];
        this.teamMaterials = [this.materialRed, this.materialBlue];
        this.teamTextMaterials = [this.materialTextRed, this.materialTextBlue];
        this.flags = [this.flagRed.getComponent(Flag)!, this.flagBlue.getComponent(Flag)!];

        this.bulletPool = this.bulletPoolObject.getComponent(BulletPool)!;

        this.audioReady = this.object.addComponent(AudioSource, {
            src: 'voice/getready.mp3',
            volume: 0.6,
        })!;
        this.audioGameStart = this.object.addComponent(AudioSource, {
            src: 'voice/letsrock.mp3',
            volume: 0.6,
        })!;
        this.audioYouWin = this.object.addComponent(AudioSource, {
            src: 'voice/youwin.mp3',
            volume: 0.6,
        })!;
        this.audioStreak = this.object.addComponent(AudioSource, {
            src: 'voice/mygod.mp3',
            volume: 0.6,
        })!;
        this.audioStreak10 = this.object.addComponent(AudioSource, {
            src: 'voice/godlike.mp3',
            volume: 0.6,
        })!;
        this.audioGameEnd = this.object.addComponent(AudioSource, {
            src: 'voice/gameover.mp3',
            volume: 0.6,
        })!;
        this.audioGameWon = this.object.addComponent(AudioSource, {
            src: 'sfx/game-win.mp3',
            volume: 0.4,
        })!;
        this.audioGameLost = this.object.addComponent(AudioSource, {
            src: 'sfx/game-lose.mp3',
            volume: 0.6,
        })!;
        this.audioWelcome = this.object.addComponent(AudioSource, {
            src: 'voice/welcome.mp3',
            volume: 0.6,
        })!;
        this.audioFlagSaved = this.object.addComponent(AudioSource, {
            src: 'voice/thatwasclose.mp3',
            volume: 0.6,
        })!;
        this.audioFlagStolen = this.object.addComponent(AudioSource, {
            src: 'voice/danger2.mp3',
            volume: 0.6,
        })!;

        this.audioFlagTaken = this.object.addComponent(AudioSource, {
            src: 'sfx/flag-taken.mp3',
            volume: 0.6,
        })!;
        this.audioScore = this.object.addComponent(AudioSource, {
            src: 'sfx/score.mp3',
            volume: 0.6,
        })!;

        this.audioGotHit = this.object.addComponent(AudioSource, {
            src: 'sfx/got-hit.mp3',
            volume: 0.4,
        })!;

        this.audioMusic = this.object.addComponent(AudioSource, {
            src: 'music/upbeat.mp3',
            spatial: false,
            volume: 0.1,
            loop: true,
        })!;

        globalAudioManager.load('sfx/splat.mp3', Sounds.Splat);

        this.connect();
    }

    override onDeactivate(): void {
        window.removeEventListener('mousedown', this.mouseDown);
        if (this.engine.xr) {
            this.engine.xr.session.removeEventListener!('selectstart', this.selectStart);
        }

        networkManager.onEvent.remove(this.onEvent);
        networkManager.client?.cleanupConnection();
        this.engine.scene.onPostRender.remove('network-manager-update');
    }

    /**
     * Function which triggers a connection initiation by calling the connect
     * function of the networkManager singleton instance.
     */
    connect() {
        GameAnalytics.addDesignEvent('game:connect:start', Date.now() - this.connectStart);

        this.connectStart = Date.now();
        const customJoinData = {
            handTracking: false,
            hands: true,
            username: this.username,
            avatar: this.avatar,
        };
        if (this.connecting) {
            console.error('cannot connect multiple times!');
            return;
        } else {
            this.connecting = true;
        }
        console.log('connecting...', {
            host: this.serverHost,
            port: this.serverPort,
            secure: this.secure,
            audio: this.audio,
            audioDeviceId: this.inputDeviceId,
            debug: this.debug,
            path: this.serverPath,
            discord: this.discord,
        });

        networkManager
            .connect(customJoinData, {
                host: this.serverHost,
                port: this.serverPort,
                secure: this.secure,
                audio: this.audio,
                audioDeviceId: this.inputDeviceId,
                debug: this.debug,
                path: this.serverPath,
                discord: this.discord,
            })
            .then(this.onSuccessfulConnection.bind(this))
            .catch((e) => {
                console.error(e);
                GameAnalytics.addDesignEvent(
                    'game:connect:fail',
                    Date.now() - this.connectStart
                );

                /* User may have switched to the menu */
                if (this.isDestroyed) return;

                /* No more updates for now. */
                this.active = false;
                this.connecting = false;

                /* Automatically reconnect -- for debugging the server */
                setTimeout(this.connect.bind(this), 1000);
            });
    }

    update(dt: number) {
        this.notificationTimer -= dt;
        if (this.notificationTimer < 0 && this.notificationText) {
            this.notificationText.text = '';
        }
    }

    override onSuccessfulConnection(joinEvent: number[]) {
        try {
            /* We are mis-using postRender here to ensure all
             * networked-component updates have been called */
            this.engine.scene.onPostRender.add(networkManager.update.bind(networkManager), {
                id: 'network-manager-update',
            });

            networkManager.onEvent.add(this.onEvent);
            this.playerHead &&
                this.playerHead.addComponent('networked', {
                    networkId: joinEvent[0],
                    mode: 'send',
                });
            /* Attach gun send component to _both_, but only activate one */
            this.playerGun &&
                this.playerGun.addComponent('networked', {
                    networkId: joinEvent[1],
                    mode: 'send',
                    active: !this.engine.xr,
                });
            this.playerGunVr &&
                this.playerGunVr.addComponent('networked', {
                    networkId: joinEvent[1],
                    mode: 'send',
                    active: !!this.engine.xr,
                });

            this.playerHand.addComponent('networked', {
                networkId: joinEvent[2],
                mode: 'send',
            });

            /* Start updating! */
            this.active = true;
            this.audioWelcome.play();

            GameAnalytics.addDesignEvent(
                'game:connect:success',
                Date.now() - this.connectStart
            );
        } catch (e) {
            console.log('Error while trying to join:', e);
            GameAnalytics.addErrorEvent(
                ErrorSeverity.Error,
                `Player failed to join:` + e.toString()
            );
        }
    }

    override onEvent = (e: WonderlandWebsocketEvent) => {
        switch (e.type) {
            case 'user-joined':
                this.onUserJoined(e);
                break;

            case 'user-left':
                const remoteUser = this.remoteUsers.get(e.data.id);
                if (remoteUser) {
                    Object.values(remoteUser).forEach(({object, id}) => {
                        networkManager.removeObject(id);
                        /* gun is destroyed with parent player already */
                        if (!object.isDestroyed) object.destroy();
                    });
                    this.notify(e.data.name.toString() + ' left the game.');
                }
                this.remoteUsers.delete(e.data.id);
                break;
            case 'shoot':
                let b = this.bulletMap.get(e.data.id);
                if (b) {
                    b.getComponent(Bullet)!.updateFromServer(
                        e.data.pos,
                        e.data.dir,
                        e.data.team
                    );
                } else {
                    b = this.createBullet(
                        e.data.origin,
                        e.data.pos,
                        e.data.dir,
                        false,
                        e.data.team
                    );
                    this.bulletMap.set(e.data.id, b);
                }

                /* This only happens when bullet debugging is on!
                 * FIXME: No bullet would show either way, since onDeactivate
                 * dectivates all bullet components */
                if (e.data.object) {
                    b.getComponent(Bullet)!.active = false;
                    b.addComponent(NetworkedComponent, {
                        networkId: e.data.object,
                        mode: 'receive',
                    });
                }
                break;
            case 'shoot-rem':
                const bObject = this.bulletMap.get(e.data.id)!;
                /* If the player joins mid-game, he will get shot removals
                 * for bullets he never saw. */
                if (bObject) {
                    /* If this local user is hit, we want to remove the
                     * splat to avoid it hiding the view */
                    let supressSplat = false;
                    if (e.data.hitUser) {
                        const userObjects = this.remoteUsers.get(e.data.hitUser);
                        if (userObjects) {
                            bObject.parent = userObjects.head.object;
                        } else {
                            /* The local player was hit, hide bullet later */
                            supressSplat = true;
                        }
                        bObject.setPositionWorld(e.data.pos);
                        bObject.setScalingWorld(BulletScaling);
                    } else {
                        bObject.setPositionLocal(e.data.pos);
                    }
                    bObject.getComponent(Bullet)!.splat(e.data.impactNormal);
                    if (supressSplat) bObject.setPositionWorld([0, -10, 0]);

                    this.bulletMap.delete(e.data.id);
                }
                break;
            case 'player-hit':
                const user = this.remoteUsers.get(e.data.id);
                if (user) {
                    const timeoutSign = user.head.object
                        .findByName('TimeoutSign')[0]!
                        .getComponent(TimeoutSign)!;
                    timeoutSign.active = true;
                    setTimeout(() => {
                        timeoutSign.active = false;
                    }, e.data.timeout);
                }
                break;
            case 'got-hit':
                this.notify('You were hit by ' + e.data.hitByName.toString() + '.');
                const timeoutScreen = this.timeoutScreen!.getComponent(TimeoutScreen)!;
                timeoutScreen.timeout = Math.ceil(e.data.timeout / 1000);
                timeoutScreen.active = true;
                this.object.getComponent(SmoothLocomotion)!.active = false;
                this.streak = 0;
                this.audioGotHit.play();
                this.timeout = true;
                break;
            case 'hit':
                this.notify('You hit ' + e.data.name.toString() + '.');
                this.hitSound.getComponent(AudioSource)?.play();

                this.streak += 1;
                if (this.streak == 5) {
                    this.audioStreak.play();
                }
                if (this.streak == 10) {
                    this.audioStreak10.play();
                }
                break;
            case 'respawn':
                this.timeout = false;
                const locomotion = this.object.getComponent(SmoothLocomotion)!;
                if (locomotion.active) locomotion.active = false;
                this.object.setPositionWorld(e.data.pos);
                locomotion.active = true;
                this.streak = 0;

                break;
            case 'leaderboard':
                const lb = e.data as LeaderboardEvent;
                this.leaderboard
                    .getComponent(Leaderboard)
                    ?.updateLeaderboard(e.data.leaderboard, lb.teams[0], lb.teams[1]);
                break;
            case 'round-start':
                this.audioReady.play();
                this.audioMusic.play();
                if (e.data.delay) {
                    this.notify('Round starting...');
                    setTimeout(() => {
                        this.onRoundStart(e.data);
                        this.audioGameStart.play();
                    }, e.data.delay);
                    GameAnalytics.addProgressionEvent(
                        ProgressionStatus.Start,
                        'game',
                        'start'
                    );
                } else {
                    this.onRoundStart(e.data);
                    this.notify('Round in progress!');
                    GameAnalytics.addProgressionEvent(
                        ProgressionStatus.Start,
                        'game',
                        'join'
                    );
                }
                break;
            case 'round-end':
                let xpGain = Config.xpInc;
                if (e.data.winner === undefined) {
                    this.notify('Tie game!');
                    GameAnalytics.addProgressionEvent(
                        ProgressionStatus.Complete,
                        'game',
                        'tie'
                    );
                } else if (this.team === e.data.winner) {
                    this.notify('You win!');
                    this.audioYouWin.play();
                    setTimeout(() => this.audioGameWon.play(), 1000);
                    /* Small bonus for winning a game */
                    xpGain += Config.xpPerWinF * Config.xpInc;
                    GameAnalytics.addProgressionEvent(
                        ProgressionStatus.Complete,
                        'game',
                        'win'
                    );
                } else {
                    this.notify('Good game!');
                    this.audioGameEnd.play();
                    setTimeout(() => this.audioGameLost.play(), 1000);
                    GameAnalytics.addProgressionEvent(
                        ProgressionStatus.Complete,
                        'game',
                        'lose'
                    );
                }
                /* Small bonus for every tag */
                const playerEntry = e.data.leaderboard.filter(
                    (e: any) => !this.remoteUsers.has(e.id)
                )[0];
                if (playerEntry) {
                    xpGain += Config.xpPerTagF * Config.xpInc * playerEntry.taggedCount;
                }

                xpGain = Math.ceil(xpGain);

                setTimeout(() => {
                    this.rewardScreen.getComponent(RewardScreen)?.gainXP(xpGain);
                    profile.addXP(xpGain);
                }, 2000);

                this.audioMusic.stop();

                this.onRoundEnd();
                break;
            case 'score':
                this.notify('Team ' + TeamName[e.data.team] + ' scored!');
                if (e.data.team == this.team) this.audioScore.play();
                break;
            case 'flag-pickup':
                this.flags[e.data.flagTeam].take();
                this.audioFlagTaken.play();
                if (e.data.flagTeam == this.team) {
                    this.notify('The enemy has the flag!');
                    this.audioFlagStolen.play();
                } else {
                    this.notify('Your team has the flag!');
                }
                break;
            case 'flag-drop':
                this.flags[e.data.flagTeam].drop(e.data.pos);
                if (e.data.flagTeam == this.team) {
                    this.notify('The enemy dropped the flag!');
                } else {
                    this.notify('Your team dropped the flag!');
                }
                break;
            case 'flag-return':
                this.flags[e.data.flagTeam].return(e.data.pos);
                if (!e.data.reset) {
                    if (e.data.flagTeam == this.team) {
                        this.notify('Your flag was returned.');
                        this.audioFlagSaved.play();
                    } else {
                        this.notify('The enemy returned their flag.');
                    }
                }
                break;
            case 'paintball-ping':
                networkManager.client?.sendViaWs({
                    type: 'paintball-pong',
                    data: e.data,
                });
                break;
            default:
                if (this.debug) console.log('Unknown event:', e);
        }
    };

    onUserJoined(e: WonderlandWebsocketEvent) {
        const userId = e.data.id;

        // ←— if we've already created an entry for this user, do nothing
        if (this.remoteUsers.has(userId)) return;

        const root = this.playerPrototype.clone();
        root.addComponent('networked', {
            networkId: e.data.networkIds[0],
            mode: 'receive',
        });
        const usernameObject = root.findByNameRecursive('Username')[0];
        usernameObject.getComponent(TextComponent)!.text = e.data.name;
        if (e.data.team) {
            usernameObject.getComponent(TextComponent)!.material =
                this.teamTextMaterials[e.data.team];
        }

        const userObjects: UserObjects = {
            head: {
                object: root,
                id: e.data.networkIds[0] as number,
            },
            gun: {},
        };
        if (e.data.networkIds.length > 1) {
            const gun = root.findByNameDirect('Gun')[0];
            gun.addComponent('networked', {
                networkId: e.data.networkIds[1],
                mode: 'receive',
            });
            userObjects.gun.object = gun;

            if (e.data.team) {
                gun.getComponent(MeshComponent)!.material = this.teamMaterials[e.data.team];
            }
        }
        if (e.data.networkIds.length > 2) {
            const hand = root.findByNameDirect('Hand')[0];
            hand.addComponent('networked', {
                networkId: e.data.networkIds[2],
                mode: 'receive',
            });

            if (e.data.team) {
                hand.getComponent(MeshComponent)!.material =
                    this.teamBulletMaterials[e.data.team];
            }
        }

        const headObject = root.findByName('Head')[0];
        const bodyObject = root.findByName('Body')[0];
        const handObject = root.findByName('Hand')[0];
        if (e.data.avatar) {
            console.error(e.data);
            // const hasvrmext =
            //     e.data.avatar.startsWith('https://') && e.data.avatar.indexof('.vrm') > 0;
            const isAvatarFromViverse = e.data.avatar.includes('avatar.viverse.com');

            const avatarUrl = e.data.avatar;

            const vrmIk = root.addComponent(PaintballVrmIk, {
                src: avatarUrl,
            });
            vrmIk!.loadVrm();

            const avatar = e.data.avatar.split('|')[0];
            headObject.getComponents(MeshComponent).forEach((c) => c.destroy());

            bodyObject.getComponents(MeshComponent).forEach((c) => c.destroy());

            handObject.getComponents(MeshComponent).forEach((c) => c.destroy());

            console.error(root);
        }

        this.remoteUsers.set(e.data.id, userObjects);
        this.notify(e.data.name.toString() + ' joined the game.');
    }
    notify(text: string) {
        if (!this.notificationText) return;
        this.notificationText.text = text;
        this.notificationTimer = this.notifyTime;
    }

    shoot() {
        /* Player may not shoot while timed out */
        if (this.timeout) return;
        TargetShootingPanel.userShots += 1;

        this.bulletStart.getPositionWorld(tempVec0);
        this.bulletStart.getForwardWorld(tempVec1);
        const BULLET_SPEED = 30;
        vec3.scale(tempVec1 as vec3, tempVec1 as vec3, BULLET_SPEED);

        const linVel = this.object.getComponent(SmoothLocomotion)!.physics.linearVelocity;

        if (networkManager.client?.dataConnected) {
            const id = Date.now();
            networkManager.client?.sendViaWs({
                type: 'shoot',
                data: {
                    pos: tempVec0,
                    dir: tempVec1,
                    linVel,
                    id,
                },
            });
            tempVec1[0] += linVel[0];
            tempVec1[1] += linVel[1];
            tempVec1[2] += linVel[2];
            const b = this.createBullet(tempVec0, tempVec0, tempVec1);
            this.bulletMap.set(id, b);
        } else {
            /* Local bullet for fun! */
            this.createBullet(tempVec0, tempVec0, tempVec1, true);
        }

        this.magAnim.stop();
        this.pinAnim.stop();
        this.magAnim.play();
        this.pinAnim.play();

        if (this.engine.xr) hapticFeedback(this.playerGunVr.parent!, 0.7, 50);
    }

    startGame() {
        if (networkManager.client?.dataConnected) {
            networkManager.client?.sendViaWs({type: 'start-game', data: {}});
        }
    }

    onRoundStart(data: {
        team: number;
        playerTeams: Record<number, number>;
        delay?: number;
        duration: number;
    }) {
        this.playerGun.getComponent(MeshComponent)!.material =
            this.playerGunVr.getComponent(MeshComponent)!.material =
                this.teamMaterials[data.team];

        this.team = data.team;

        this.menuButton.active = false;
        this.menuButton.children.forEach((c) => (c.active = false));
        this.startButton.active = false;

        //this.startButton.findByNameDirect('Text').getComponent('text');
        const timer = this.startButton.findByNameDirect('Text')[0].getComponent(Timer)!;
        timer.timerEnd = Date.now() + data.duration;
        timer.active = true;

        if (!data.playerTeams) return;
        Object.entries(data.playerTeams).forEach(([k, t]) => {
            /* This is actually an int in remoteUsers, but the types
             * are inacurate */
            const userId = Number.parseInt(k) as unknown as string;
            const user = this.remoteUsers.get(userId) as unknown as UserObjects;
            if (user && user.gun.object) {
                user.gun.object.getComponent(MeshComponent)!.material =
                    this.teamMaterials[t];
            }
            if (user && user.head.object) {
                user.head.object
                    .findByNameRecursive('Username')[0]
                    .getComponent(TextComponent)!.material = this.teamTextMaterials[t];
            }
        });
    }

    onRoundEnd() {
        const startButtonText = this.startButton.findByNameDirect('Text')[0];
        const timer = startButtonText.getComponent(Timer)!;
        timer.active = false;

        this.menuButton.active = true;
        this.menuButton.children.forEach((c) => (c.active = true));
        this.startButton.active = true;
        startButtonText.getComponent('text')!.text = 'Start\nGame';
    }

    createBullet(
        origin: number[],
        pos: number[],
        dir: number[],
        offline = false,
        team: Team = this.team
    ) {
        const bullet = this.bulletPool.shoot();
        bullet.object.setPositionWorld(pos);

        bullet.direction[0] = dir[0];
        bullet.direction[1] = dir[1];
        bullet.direction[2] = dir[2];
        bullet.offline = offline;
        bullet.active = true;

        /* We update the material late, since we need to have
         * created the trail in start() */
        bullet.updateMaterial(team);

        this.shotSound.setPositionLocal(origin);
        const shotSound = this.shotSound.getComponent(AudioSource);
        if (shotSound) shotSound.play();
        else console.warn('No shot audio component found.');
        return bullet.object;
    }
}
